import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button } from 'react-bootstrap';
import { IoCheckmarkCircle, IoDocumentOutline, IoChevronDownOutline } from 'react-icons/io5';
import { setPackagingUnitDrawingDocument } from '../../../../store/actions/packagingUnit';
import { successGlobal } from '../../../../store/reducers/notifications';

const DrawingDocumentSection = ({ unit }) => {
    const dispatch = useDispatch();
    const uploadedDocuments = useSelector(state => state.packagingUnitDocument.data) || [];

    const currentDocId = unit.drawingDocumentId ? String(unit.drawingDocumentId) : '';
    const currentDoc = uploadedDocuments.find(d => String(d._id) === currentDocId) || null;
    const currentNotProvided = !!unit.drawingDocumentNotProvided;
    const isComplete = !!currentDocId || currentNotProvided;

    const [open, setOpen] = useState(!isComplete);
    const [selected, setSelected] = useState(currentDocId);
    const [notProvided, setNotProvided] = useState(currentNotProvided);
    const [saving, setSaving] = useState(false);

    const isDirty = selected !== currentDocId || notProvided !== currentNotProvided;

    const handleSelectDocument = (documentId) => {
        setSelected(documentId);
        if (documentId) setNotProvided(false);
    };

    const handleToggleNotProvided = (checked) => {
        setNotProvided(checked);
        if (checked) setSelected('');
    };

    const handleSave = () => {
        setSaving(true);
        dispatch(setPackagingUnitDrawingDocument({ packagingUnitId: unit._id, documentId: selected || null, notProvided }))
            .unwrap()
            .then(() => dispatch(successGlobal('Výkresová dokumentácia bola uložená.')))
            .catch(() => {})
            .finally(() => setSaving(false));
    };

    return (
        <div className="mb-4" style={{ border: '1px solid #EEF0F8', borderRadius: 14, overflow: 'hidden' }}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="d-flex align-items-center gap-3 w-100"
                style={{
                    border: 'none', textAlign: 'left', cursor: 'pointer',
                    backgroundColor: isComplete ? '#E6F4EA' : '#F5F6FB',
                    padding: '12px 16px',
                }}
            >
                {isComplete
                    ? <IoCheckmarkCircle size={22} style={{ color: '#198754', flexShrink: 0 }} />
                    : <IoDocumentOutline size={22} style={{ color: '#8A8FA8', flexShrink: 0 }} />}
                <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: isComplete ? '#198754' : '#5B6178' }}>
                            b) Výkresová dokumentácia
                        </span>
                    </div>
                    {!open && (
                        <div className="text-muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                            {currentDoc
                                ? currentDoc.originalName
                                : currentNotProvided
                                    ? 'Nebola dodaná dodávateľom'
                                    : 'Nie je priradený žiadny dokument'}
                        </div>
                    )}
                </div>
                <IoChevronDownOutline
                    size={16}
                    style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
                />
            </button>

            {open && (
                <div style={{ padding: '14px 16px', background: '#fff' }}>
                    {currentDoc && (
                        <div className="d-flex align-items-center gap-2 mb-3 p-2"
                            style={{ background: '#E6F4EA', borderRadius: 8, fontSize: 12.5 }}>
                            <IoCheckmarkCircle size={15} style={{ color: '#198754', flexShrink: 0 }} />
                            <span style={{ color: '#198754', fontWeight: 600 }}>Aktuálne priradený:</span>
                            <span style={{ color: '#2D4739' }}>{currentDoc.originalName}</span>
                            {currentDoc.checklistItemLabel && (
                                <span className="text-muted">— {currentDoc.checklistItemLabel}</span>
                            )}
                        </div>
                    )}

                    <Form.Check
                        type="switch"
                        id="drawing-document-not-provided"
                        label="Nebola dodaná dodávateľom"
                        className="mb-3"
                        checked={notProvided}
                        onChange={e => handleToggleNotProvided(e.target.checked)}
                    />

                    {!notProvided && (uploadedDocuments.length === 0 ? (
                        <p className="text-muted mb-3" style={{ fontSize: 12.5, fontStyle: 'italic' }}>
                            Zatiaľ nemáte nahraté žiadne dokumenty. Nahrajte ich v záložke „Doklady od dodávateľov".
                        </p>
                    ) : (
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 12.5, fontWeight: 600, color: '#5B6178' }}>
                                Vybrať dokument z priložených dokladov
                            </Form.Label>
                            <Form.Select
                                size="sm"
                                value={selected}
                                onChange={e => handleSelectDocument(e.target.value)}
                                style={{ maxWidth: 480 }}
                            >
                                <option value="">— žiadny —</option>
                                {uploadedDocuments.map(doc => (
                                    <option key={doc._id} value={doc._id}>
                                        {doc.originalName}{doc.checklistItemLabel ? ` — ${doc.checklistItemLabel}` : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    ))}

                    <div className="d-flex justify-content-end">
                        <Button
                            className="btn-custom-small"
                            disabled={!isDirty || saving}
                            onClick={handleSave}
                        >
                            {saving ? 'Ukladá sa...' : 'Uložiť'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DrawingDocumentSection;
